import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Check, ChevronRight, LogOut, ShieldCheck, Sparkles, Users } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '../../../components/ui/drawer';
import { Separator } from '../../../components/ui/separator';
import { Skeleton } from '../../../components/ui/skeleton';
import { useIsMobile } from '../../../components/ui/use-mobile';
import { ROUTES } from '../../../shared/config/routes';
import { useCurrentUser } from '../../../shared/hooks/useCurrentUser';
import {
  initialsOf,
  loginOf,
  useSignedInEmail,
} from '../../../shared/hooks/useSignedInEmail';
import { useAccountStore } from '../../../entities/account';
import { supabase } from '../../../shared/api/supabase-client';
import { flushMontagePending, resetMontageStore } from '../../../features/montage/store';
import { ME_HEAD_OF, ME_MODERATOR_OF, ME_ROLE } from '../../../features/montage/data/people';
import { profileApi, type UserProfile } from '../../../shared/api/profile.api';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import { ChangelogDialog, useChangelogSeen } from '../../../features/changelog';
import { readQuery, writeQuery } from '../../../shared/lib/queryCache';
import type { PipelineContext } from '../../../shared/api/tasks.api';

interface ProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function syncProfileInNavigation(patch: { fullName?: string | null; avatarUrl?: string | null }): void {
  const cached = readQuery<PipelineContext>('pipeline:context');
  if (!cached) return;
  writeQuery('pipeline:context', { ...cached.data, user: { ...cached.data.user, ...patch } });
}

/**
 * Профиль — окно, а не экран. Заходят сюда посмотреть «кто я и за что отвечаю»
 * и выйти; ради этого уводить человека со страницы, на которой он работает,
 * незачем — и возвращаться потом некуда.
 *
 * На телефоне это шторка снизу, на компьютере — обычный диалог.
 */
export function ProfileDialog({ open, onOpenChange }: ProfileDialogProps): JSX.Element {
  const isMobile = useIsMobile();
  // «Что нового» на телефоне: сайдбара с этим пунктом там нет.
  const [changelogOpen, setChangelogOpen] = useState(false);
  const { hasUnread, markSeen } = useChangelogSeen();
  const openChangelog = () => {
    onOpenChange(false);
    markSeen();
    setChangelogOpen(true);
  };

  if (isMobile) {
    return (
      <>
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader className="sr-only">
            <DrawerTitle>Профиль</DrawerTitle>
            <DrawerDescription>Кто вы в системе и за какие аккаунты отвечаете</DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto px-4 pb-6">
            <ProfileBody onNavigate={() => onOpenChange(false)} changelog={{ hasUnread, open: openChangelog }} />
          </div>
        </DrawerContent>
      </Drawer>
      <ChangelogDialog open={changelogOpen} onOpenChange={setChangelogOpen} />
      </>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto p-0 sm:max-w-md">
        <DialogHeader className="sr-only">
          <DialogTitle>Профиль</DialogTitle>
          <DialogDescription>Кто вы в системе и за какие аккаунты отвечаете</DialogDescription>
        </DialogHeader>
        <div className="p-4">
          <ProfileBody onNavigate={() => onOpenChange(false)} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Содержимое окна — общее для шторки и диалога. */
function ProfileBody({ onNavigate, changelog }: {
  onNavigate: () => void;
  /** Только на телефоне: на компьютере «Что нового» — в сайдбаре. */
  changelog?: { hasUnread: boolean; open: () => void };
}): JSX.Element {
  const { isAdmin, assignedAccountIds, isLoading: isUserLoading } = useCurrentUser();
  const accounts = useAccountStore((state) => state.accounts);
  const isAccountsLoading = useAccountStore((state) => state.isLoading);
  const email = useSignedInEmail();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [nickname, setNickname] = useState('');
  const [saving, setSaving] = useState(false);
  const avatarInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    profileApi.get().then(({ profile }) => {
      setProfile(profile);
      setNickname(profile.full_name ?? profile.username ?? '');
    }).catch((error) => toast.error((error as Error).message));
  }, []);

  const myAccounts = useMemo(() => {
    if (isAdmin) return accounts;
    const assigned = new Set(assignedAccountIds);
    return accounts.filter((account) => assigned.has(account.id));
  }, [accounts, assignedAccountIds, isAdmin]);

  const isLoadingAccounts = isAccountsLoading && accounts.length === 0;
  const roleLabel = isAdmin
    ? 'Администратор'
    : ME_HEAD_OF.length > 0
      ? 'Руководитель'
      : ME_MODERATOR_OF.length > 0 || ME_ROLE === 'moderator'
        ? 'Модератор'
        : ME_ROLE === 'editor'
          ? 'Монтажёр'
          : ME_ROLE === 'client'
            ? 'Контент-мейкер'
            : 'Сотрудник';

  return (
    <div className="flex flex-col gap-4">
      {/* pr-7 — место для крестика диалога: он висит абсолютом в углу и
          иначе наезжает на роль. */}
      <div className="flex items-center gap-3 pr-7">
        <button type="button" className="group relative shrink-0" onClick={() => avatarInput.current?.click()} title="Сменить аватар">
          <Avatar className="size-11">
            <AvatarImage src={profile?.avatar_url ?? undefined} alt="" />
            <AvatarFallback className="font-semibold">
              {initialsOf(profile?.full_name ?? (email ? loginOf(email) : '?'))}
            </AvatarFallback>
          </Avatar>
          <span className="absolute inset-0 grid place-items-center rounded-full bg-black/45 text-white opacity-0 transition-opacity group-hover:opacity-100"><Camera className="size-4" /></span>
          <input
            ref={avatarInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setSaving(true);
              profileApi.uploadAvatar(file)
                .then(({ avatarUrl }) => {
                  setProfile((p) => p ? { ...p, avatar_url: avatarUrl } : p);
                  syncProfileInNavigation({ avatarUrl });
                  toast.success('Аватар обновлён');
                })
                .catch((error) => toast.error((error as Error).message))
                .finally(() => setSaving(false));
              e.currentTarget.value = '';
            }}
          />
        </button>

        <div className="min-w-0 flex-1">
          {email ? (
            <>
              <p className="truncate font-medium">{profile?.full_name ?? loginOf(email)}</p>
              <p className="truncate text-sm text-muted-foreground">@{profile?.username ?? loginOf(email)}</p>
            </>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3.5 w-44" />
            </div>
          )}
        </div>

        {isUserLoading ? (
          <Skeleton className="h-[22px] w-24 shrink-0 rounded-md" />
        ) : (
          <Badge variant={isAdmin ? 'default' : 'secondary'}>
            {isAdmin && <ShieldCheck />}
            {roleLabel}
          </Badge>
        )}
      </div>

      <Separator />

      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium">Никнейм</p>
        <div className="flex gap-2">
          <Input value={nickname} maxLength={60} onChange={(e) => setNickname(e.target.value)} placeholder="Как вас показывать команде" />
          <Button
            size="icon"
            disabled={saving || nickname.trim().length < 2 || nickname.trim() === profile?.full_name}
            onClick={() => {
              setSaving(true);
              profileApi.updateNickname(nickname.trim())
                .then(({ profile }) => {
                  setProfile(profile);
                  setNickname(profile.full_name ?? '');
                  syncProfileInNavigation({ fullName: profile.full_name });
                  toast.success('Никнейм сохранён');
                })
                .catch((error) => toast.error((error as Error).message))
                .finally(() => setSaving(false));
            }}
            title="Сохранить никнейм"
          ><Check /></Button>
        </div>
        <p className="text-xs text-muted-foreground">Логин остаётся только для входа; в задачах команда видит никнейм.</p>
      </div>

      <Separator />

      <div className="flex flex-col gap-1">
        <p className="px-1 text-sm font-medium">
          {isAdmin ? 'Все аккаунты' : 'Мои аккаунты'}
          {!isLoadingAccounts && myAccounts.length > 0 && (
            <span className="ml-1.5 text-muted-foreground">{myAccounts.length}</span>
          )}
        </p>

        {isLoadingAccounts ? (
          <div className="flex flex-col gap-3 p-1">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="size-9 rounded-full" />
                <div className="flex flex-col gap-1.5">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3.5 w-20" />
                </div>
              </div>
            ))}
          </div>
        ) : myAccounts.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
            <Users className="size-8 text-muted-foreground" />
            <p className="font-medium">За вами пока не закреплён ни один аккаунт</p>
            <p className="text-sm text-muted-foreground">
              Доступы раздаёт администратор в разделе «Сотрудники и доступы». Напишите ему —
              и аккаунты появятся здесь.
            </p>
          </div>
        ) : (
          <div className="-mx-1 flex max-h-72 flex-col overflow-y-auto">
            {myAccounts.map((account) => (
              <Button
                key={account.id}
                asChild
                variant="ghost"
                className="h-auto justify-start gap-3 px-2 py-2 font-normal"
                onClick={onNavigate}
              >
                <Link to={`/analytics/account/${account.id}`}>
                  <Avatar className="size-9">
                    <AvatarImage src={account.profilePicture} alt="" />
                    <AvatarFallback className="text-xs font-semibold">
                      {initialsOf(account.username || account.name || '?')}
                    </AvatarFallback>
                  </Avatar>

                  <span className="flex min-w-0 flex-1 flex-col text-left">
                    <span className="truncate font-medium">
                      {account.username ? `@${account.username}` : account.name}
                    </span>
                    <span className="truncate text-sm text-muted-foreground">
                      {(account.followersCount ?? 0).toLocaleString('ru-RU')} подписчиков
                      {!account.isActive && ' · не активен'}
                    </span>
                  </span>

                  <ChevronRight className="text-muted-foreground" />
                </Link>
              </Button>
            ))}
          </div>
        )}
      </div>

      <Separator />

      <div className="flex flex-col gap-1">
        {changelog && (
          <Button variant="ghost" className="justify-start" onClick={changelog.open}>
            <Sparkles />
            Что нового
            {changelog.hasUnread && (
              <span aria-label="есть непрочитанное" className="ml-auto size-1.5 shrink-0 rounded-full bg-primary" />
            )}
          </Button>
        )}
        {isAdmin && (
          <Button asChild variant="ghost" className="justify-start" onClick={onNavigate}>
            <Link to={ROUTES.TEAM}>
              <ShieldCheck />
              Сотрудники и доступы
            </Link>
          </Button>
        )}
        <Button
          variant="ghost"
          className="justify-start text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={() => void (async () => {
            await flushMontagePending();
            resetMontageStore();
            await supabase.auth.signOut();
          })()}
        >
          <LogOut />
          Выйти
        </Button>
      </div>
    </div>
  );
}
